import { useCallback, useEffect, useMemo, useState } from "react";
import { UpdateOrderStatus } from "../../services/order";
import {
  IPhoneOrderLedgerItem, IPhoneOrderLedgerPage, PHONE_LEDGER_DEFAULT_PAGE_SIZE,
  PHONE_LEDGER_MAX_WINDOW_DAYS, PHONE_ORDER_NEXT_STEP, PHONE_ORDER_STATUS, phonePaymentLabel,
} from "../models/IPhoneOrder";
import { getPhoneOrderLedger, phoneErrorMessage } from "../services/phoneOrderService";
import PhoneOrderDetailModal, { STATUS_ERROR } from "./PhoneOrderDetailModal";
import PhoneOrderPaymentModal from "./PhoneOrderPaymentModal";

const LEDGER_ERROR: Record<string, string> = {
  IncompleteDateRange: "Tarih aralığının iki ucu da seçilmeli.",
  InvalidPagination: "Sayfalama değerleri geçersiz.",
  RestourantAccessDenied: "Bu restoranın telefon siparişlerini görme yetkiniz yok.",
};

const pad = (n: number) => String(n).padStart(2, "0");
const toDateOnly = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function money(n: number, symbol?: string | null): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

function daysBetweenInclusive(from: string, to: string): number {
  const a = new Date(from + "T00:00:00");
  const b = new Date(to + "T00:00:00");
  return Math.floor((b.getTime() - a.getTime()) / 86400000) + 1;
}

/** Sunucunun verdiği yerel saati gösterir; ekran kendisi dönüştürmez. */
function localTime(orderDateLocal: string): string {
  const [datePart, timePart] = orderDateLocal.split("T");
  if (!datePart || !timePart) return orderDateLocal;
  const [y, m, d] = datePart.split("-");
  return `${d}.${m}.${y} ${timePart.slice(0, 5)}`;
}

type Preset = "today" | "yesterday" | "last7" | "thisMonth" | "custom";

function presetRange(p: Exclude<Preset, "custom">): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (p === "today") return { from: toDateOnly(today), to: toDateOnly(today) };
  if (p === "yesterday") {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { from: toDateOnly(y), to: toDateOnly(y) };
  }
  if (p === "last7") {
    const s = new Date(today); s.setDate(s.getDate() - 6);
    return { from: toDateOnly(s), to: toDateOnly(today) };
  }
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: toDateOnly(first), to: toDateOnly(today) };
}

interface Props {
  restaurantId: string;
}

export default function PhoneOrderLedgerTab({ restaurantId }: Props) {
  const [preset, setPreset] = useState<Preset>("today");
  const [from, setFrom] = useState(toDateOnly(new Date()));
  const [to, setTo] = useState(toDateOnly(new Date()));
  const [page, setPage] = useState(1);
  const [data, setData] = useState<IPhoneOrderLedgerPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState<IPhoneOrderLedgerItem | null>(null);
  const [fixing, setFixing] = useState<IPhoneOrderLedgerItem | null>(null);
  const [quickBusyId, setQuickBusyId] = useState<string | null>(null);

  const windowDays = useMemo(() => (from && to ? daysBetweenInclusive(from, to) : 0), [from, to]);
  const rangeInvalid = !!from && !!to && (windowDays < 1 || windowDays > PHONE_LEDGER_MAX_WINDOW_DAYS);

  const load = useCallback((f: string | undefined, t: string | undefined, p: number) => {
    setLoading(true);
    setError("");
    getPhoneOrderLedger({ restaurantId, from: f, to: t, page: p, pageSize: PHONE_LEDGER_DEFAULT_PAGE_SIZE })
      .then((res) => setData(res.data))
      .catch((err) => {
        setData(null);
        setError(phoneErrorMessage(err, LEDGER_ERROR, "Telefon siparişleri alınamadı."));
      })
      .finally(() => setLoading(false));
  }, [restaurantId]);

  // İlk açılış: parametresiz çağrı sunucunun "bugün"ünü uygular.
  useEffect(() => { setPage(1); load(undefined, undefined, 1); }, [load]);

  const applyPreset = (p: Exclude<Preset, "custom">) => {
    const r = presetRange(p);
    setPreset(p); setFrom(r.from); setTo(r.to); setPage(1);
    load(r.from, r.to, 1);
  };

  const applyCustom = () => {
    if (rangeInvalid || !from || !to) return;
    setPreset("custom"); setPage(1);
    load(from, to, 1);
  };

  const goPage = (p: number) => {
    setPage(p);
    load(from, to, p);
  };

  const reload = () => load(from, to, page);

  // Satırdaki tek dokunuşla bir sonraki adıma geçiş (Hazırlanıyor → Yolda → Teslim edildi).
  const quickAdvance = (o: IPhoneOrderLedgerItem, to: string) => {
    setQuickBusyId(o.orderId);
    setError("");
    UpdateOrderStatus(o.orderId, to)
      .then(() => reload())
      .catch((err) => setError(phoneErrorMessage(err, STATUS_ERROR, "Durum güncellenemedi.")))
      .finally(() => setQuickBusyId(null));
  };

  const summary = data?.summary;
  // Hazırlanan ya da yolda olanlar: toplamdan teslim edilen ve iptal edilenler düşülür.
  const activeCount = summary
    ? Math.max(0, summary.orderCount - summary.revenueOrderCount - summary.cancelledOrderCount)
    : 0;

  return (
    <div className="po-ledger">
      {/* Aralık */}
      <div className="rev-toolbar">
        <div className="rev-presets">
          {([["today", "Bugün"], ["yesterday", "Dün"], ["last7", "Son 7 gün"], ["thisMonth", "Bu ay"]] as [Exclude<Preset, "custom">, string][])
            .map(([key, label]) => (
              <button key={key} type="button"
                className={`rev-chip${preset === key ? " rev-chip--active" : ""}`}
                onClick={() => applyPreset(key)} disabled={loading}>
                {label}
              </button>
            ))}
        </div>
        <div className="rev-range">
          <input type="date" value={from} max={to || undefined}
            onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} />
          <span>–</span>
          <input type="date" value={to} min={from || undefined}
            onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} />
          <button type="button" className="dinein-btn dinein-btn--primary"
            onClick={applyCustom} disabled={loading || rangeInvalid || !from || !to}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-search me-1"></i>Getir</>}
          </button>
        </div>
      </div>

      {rangeInvalid && (
        <div className="dinein-alert dinein-alert--warn">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>
            {windowDays < 1
              ? "Bitiş tarihi başlangıçtan önce olamaz."
              : `Aralık en fazla ${PHONE_LEDGER_MAX_WINDOW_DAYS} gün olabilir (şu an ${windowDays} gün).`}
          </span>
        </div>
      )}

      {error && (
        <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
      )}

      {loading && !data ? (
        <div className="dinein-center dinein-center--tall"><span className="spinner-border" /></div>
      ) : !data ? null : (
        <>
          {/* Pencere özeti — sayfa değişince değişmez */}
          <div className="po-sum">
            <div className="po-sum__head">
              <span>
                <i className="bi bi-calendar-range me-2"></i>
                {data.from === data.to ? data.from : `${data.from} – ${data.to}`}
              </span>
              <button className="dinein-btn dinein-btn--ghost" onClick={reload} disabled={loading}>
                <i className={`bi bi-arrow-clockwise${loading ? " dinein-spin" : ""} me-1`}></i>Yenile
              </button>
            </div>

            <div className="po-stats">
              <div className="po-stat po-stat--active">
                <span className="po-stat__label"><i className="bi bi-fire me-1"></i>Devam eden</span>
                <strong className="po-stat__value">{activeCount}</strong>
              </div>
              <div className="po-stat po-stat--ok">
                <span className="po-stat__label"><i className="bi bi-house-check me-1"></i>Teslim edildi</span>
                <strong className="po-stat__value">{summary?.revenueOrderCount ?? 0}</strong>
              </div>
              <div className="po-stat po-stat--cancel">
                <span className="po-stat__label"><i className="bi bi-x-circle me-1"></i>İptal</span>
                <strong className="po-stat__value">{summary?.cancelledOrderCount ?? 0}</strong>
              </div>
              <div className="po-stat po-stat--money">
                <span className="po-stat__label"><i className="bi bi-cash-stack me-1"></i>Ciro</span>
                {summary && summary.totals.length > 0 ? summary.totals.map((t, i) => (
                  <strong key={`${t.currencyId}-${i}`} className="po-stat__value">
                    {money(t.totalAmount, t.currencyDisplayText)}
                  </strong>
                )) : (
                  <strong className="po-stat__value">0,00</strong>
                )}
                <span className="po-stat__hint">yalnız teslim edilenler</span>
              </div>
            </div>

            {summary && summary.paymentMethods.length > 0 && (
              <div className="po-sum__methods">
                {summary.paymentMethods.map((pm, i) => (
                  <div key={`${pm.paymentMethodId}-${pm.currencyId}-${i}`}
                    className={`po-method${pm.isUnspecified ? " po-method--unspec" : ""}`}>
                    <span className="po-method__name">
                      {phonePaymentLabel(pm.paymentMethodId, pm.paymentMethodNameTr)}
                    </span>
                    <strong>{money(pm.totalAmount, pm.currencyDisplayText)}</strong>
                    <span className="po-method__count">{pm.count} sipariş</span>
                  </div>
                ))}
                <div className="dinein-hint">
                  <i className="bi bi-info-circle me-1"></i>
                  "Belirlenmedi" kovası toplamlardan düşmez; para tahsil edildi, yöntemi bilinmiyor.
                </div>
              </div>
            )}
          </div>

          {/* Satırlar */}
          {data.items.length === 0 ? (
            <div className="dinein-center dinein-center--tall">
              <i className="bi bi-telephone-x"></i>
              <div>Bu aralıkta telefon siparişi yok.</div>
            </div>
          ) : (
            <div className="po-rows">
              {data.items.map((o) => {
                const statusId = o.orderStatusId.toLowerCase();
                const next = o.isCancelled ? undefined : PHONE_ORDER_NEXT_STEP[statusId];
                const tone = o.isCancelled ? "cancel" : o.countsTowardRevenue ? "done"
                  : statusId === PHONE_ORDER_STATUS.onTheWay ? "way" : "prep";
                return (
                  <div key={o.orderId} className={`po-card po-card--${tone}`}>
                    <button type="button" className="po-card__main" onClick={() => setViewing(o)}>
                      <div className="po-card__top">
                        <span className="po-card__code">{o.orderCode}</span>
                        <span className={`po-pill po-pill--${tone}`}>{o.orderStatusNameTr.replace(/\.$/, "")}</span>
                        <span className="po-card__time"><i className="bi bi-clock me-1"></i>{localTime(o.orderDateLocal)}</span>
                      </div>
                      <div className="po-card__cust">{o.customerFullName}</div>
                      <div className="po-card__content">{o.contentSummary}</div>
                      <div className="po-card__meta">
                        <span><i className="bi bi-geo-alt me-1"></i>{o.deliveryAddress}</span>
                      </div>
                    </button>
                    <div className="po-card__side">
                      <strong className="po-card__total">{money(o.totalAmount, o.currencyDisplayText)}</strong>
                      <span className={`po-card__pay${o.isPaymentMethodSpecified ? "" : " po-card__pay--missing"}`}>
                        {phonePaymentLabel(o.paymentMethodId, o.paymentMethodNameTr)}
                      </span>
                      <div className="po-card__actions">
                        <button className="dinein-btn dinein-btn--ghost dinein-btn--sm" onClick={() => setViewing(o)}>
                          <i className="bi bi-receipt me-1"></i>Detay
                        </button>
                        {next && (
                          <button className="dinein-btn dinein-btn--primary dinein-btn--sm"
                            disabled={quickBusyId === o.orderId}
                            onClick={() => quickAdvance(o, next.to)}>
                            {quickBusyId === o.orderId
                              ? <span className="spinner-border spinner-border-sm" />
                              : <><i className={`bi ${next.icon} me-1`}></i>{next.label}</>}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {data.totalPages > 1 && (
            <div className="po-pager">
              <button className="dinein-btn dinein-btn--ghost" disabled={loading || data.page <= 1}
                onClick={() => goPage(data.page - 1)}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <span>{data.page} / {data.totalPages} · {data.totalCount} kayıt</span>
              <button className="dinein-btn dinein-btn--ghost" disabled={loading || data.page >= data.totalPages}
                onClick={() => goPage(data.page + 1)}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}

          <div className="dinein-hint" style={{ marginTop: 12 }}>
            <i className="bi bi-info-circle me-1"></i>
            Özet, açık sayfanın değil seçili tarih aralığının tamamının toplamıdır. Telefon siparişleri
            panelin "Siparişler" listesinde görünmez.
          </div>
        </>
      )}

      {viewing && !fixing && (
        <PhoneOrderDetailModal
          order={viewing}
          onClose={() => setViewing(null)}
          onChanged={reload}
          onFixPayment={(o) => setFixing(o)}
        />
      )}

      {fixing && (
        <PhoneOrderPaymentModal
          order={fixing}
          onClose={() => setFixing(null)}
          onUpdated={(updated) => {
            // Detay popup'ı açıksa yeni yöntemi hemen göstersin.
            setViewing((v) => v && v.orderId === updated.orderId ? {
              ...v,
              paymentMethodId: updated.paymentMethodId,
              paymentMethodNameTr: updated.paymentMethodNameTr,
              isPaymentMethodSpecified: updated.isPaymentMethodSpecified,
            } : v);
            reload();
          }}
        />
      )}
    </div>
  );
}
